import React, { useState, useEffect } from "react";
import { Box, Flex, Button, Grid, Tooltip } from "@chakra-ui/react";
import { getSupabase, supabaseSchema } from "./supabaseClient";
import {
  tableName,
  getBackendURL,
  navy_blue,
  royal_blue,
  turquoise,
} from "./env";
import {
  filterRowsForPatient,
  isInGroup,
  isPatientRole,
  getLabel,
  hasClinicalAdminPermissions,
} from "../config/roles";
import { fetchEffectiveUserRow } from "../utils/effectiveUserRole";
import { useSelectedProviders } from "../hooks/useSelectedProviders";
import { sharedUsersOverlapOrClauses } from "../utils/shareAccess";

// Date where the seeded test patients live (Foothill Thursday-demo fixtures
// + Kenji). The "Test" button jumps the calendar here so we don't have to
// re-seed patients to today's date. Update when the demo/fixture date changes.
const TEST_PATIENT_DATE = '2026-06-27';

/** Rows -> { 'YYYY-MM-DD': { [schema]: count } } for one schema. */
const tallyByDate = (rows, schemaName) =>
  (rows || []).reduce((acc, record) => {
    const day = (acc[record.date_] = acc[record.date_] || {});
    day[schemaName] = (day[schemaName] || 0) + 1;
    return acc;
  }, {});

/** Total rows on a day, across every schema counted for it. */
const totalForDay = (bySchema) =>
  Object.values(bySchema || {}).reduce((sum, n) => sum + n, 0);

/**
 * Schemas with at least one row that day, largest first.
 * A schema contributing nothing is left out entirely rather than shown as 0.
 */
const contributingSchemas = (bySchema) =>
  Object.entries(bySchema || {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

// Distinguishable colors assigned to schemas; a schema keeps the same color
// everywhere on the calendar regardless of which days it appears on.
// Stays inside the brand's blue family (logo colors first), on a white
// tooltip so even navy stays readable. Turquoise is excluded — it would
// vanish against the turquoise day-cell background.
const SCHEMA_COLORS = [
  royal_blue, // #4169E1 (logo)
  navy_blue, // #000080 (logo)
  "#00B5D8", // cyan
  "#38BDF8", // sky blue
  "#00D9A6", // teal-mint
  "#7C83FF", // bright indigo
];

/**
 * { schema: color } for every schema present in the month's counts. Assigning
 * by sorted position (rather than hashing the name) guarantees two schemas
 * never share a color until the palette itself runs out.
 */
const buildSchemaColorMap = (countsByDate) => {
  const names = new Set();
  for (const day of Object.values(countsByDate || {})) {
    for (const name of Object.keys(day)) names.add(name);
  }
  const map = {};
  [...names].sort().forEach((name, i) => {
    map[name] = SCHEMA_COLORS[i % SCHEMA_COLORS.length];
  });
  return map;
};

const CustomCalendar = ({ date, onSelectDate }) => {
  const [displayedMonth, setDisplayedMonth] = useState(
    new Date(date).getMonth(),
  );
  const [displayedYear, setDisplayedYear] = useState(
    new Date(date).getFullYear(),
  );
  const [patientCounts, setPatientCounts] = useState({});
  // The providers dropdown selection. Day counts used to come from the home
  // schema alone, so a doctor with colleagues ticked saw only their own rows.
  const { selected: selectedSchemas } = useSelectedProviders();
  // Join for the effect dep: a new array identity every render would refetch
  // on every render.
  const selectedKey = (selectedSchemas || []).join(",");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userRole, setUserRole] = useState(null);
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;

  const timeZoneAbbr = now
    .toLocaleString("en-US", { timeZoneName: "short" })
    .split(" ")
    .pop();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const d = new Date(date);
    if (!isNaN(d.getTime())) {
      setDisplayedMonth(d.getMonth());
      setDisplayedYear(d.getFullYear());
    }
  }, [date]);

  const formattedTime = currentTime.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  useEffect(() => {
    // Set when a newer run starts (the providers selection resolving just
    // after mount, a month change); the clinic counts below check it.
    let cancelled = false;
    const fetchPatientCounts = async () => {
      const supabase = await getSupabase();
      const startOfMonth = `${displayedYear}-${String(displayedMonth + 1).padStart(2, "0")}-01`;
      const endOfMonth = `${displayedYear}-${String(displayedMonth + 1).padStart(2, "0")}-${new Date(
        displayedYear,
        displayedMonth + 1,
        0,
      ).getDate()}`;

      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError || !sessionData?.session?.user) {
        setPatientCounts({});
        return;
      }

      const { data: userData, error: userError } = await fetchEffectiveUserRow({
        id: sessionData.session.user.id,
      });
      const role = !userError && userData?.role ? userData.role : null;

      setUserRole(role);

      const userId = sessionData.session.user.id;

      // ── Patients: always their own verified schema. The providers dropdown
      // is a clinic-side control and does not apply to them.
      if (isPatientRole(role)) {
        if (userData?.patient_match_status !== "verified" || !userData?.patient_dob) {
          setPatientCounts({});
          return;
        }
        const { data } = await supabase
          .schema(userData.patient_verified_schema)
          .from(tableName)
          .select("*")
          .gte("date_", startOfMonth)
          .lte("date_", endOfMonth)
          .eq("dob", String(userData.patient_dob).slice(0, 10));
        setPatientCounts(
          tallyByDate(
            filterRowsForPatient(userData, data || []),
            userData.patient_verified_schema,
          ),
        );
        return;
      }

      // ── Clinic roles: one query per ticked provider, summed. A schema the
      // user cannot read is skipped rather than blanking the whole calendar,
      // so the remaining providers still show their counts.
      //
      // .schema(s) is passed explicitly on every query: supabase-js mutates the
      // shared postgrest client in place, so a bare .from() after this loop
      // would read whichever schema happened to go last.
      const schemas = (selectedSchemas || []).length ? selectedSchemas : [null];

      // Rows shared with this user come from a per-user endpoint, not a
      // per-schema one — fetch once rather than once per provider.
      let ownerIds = [];
      const needsSharing =
        !hasClinicalAdminPermissions(role) && isInGroup(role, "tenantSchema");
      if (needsSharing) {
        try {
          const backendUrl = getBackendURL();
          const res = await fetch(
            `${backendUrl}/api/shared-all-owner-ids/?user_id=${encodeURIComponent(userId)}`,
          );
          const json = await res.json().catch(() => ({}));
          if (res.ok && Array.isArray(json.owner_ids)) ownerIds = json.owner_ids;
        } catch (_) {}
      }

      // All at once: each query is a full round trip and none waits on
      // another. date_ is the only column the tally reads, and rows without an
      // id are never overlaid (rdsPhiOverlay.js overlayResult), so a month of
      // counts no longer pulls its visits through RDS.
      const queries = [];
      for (const schemaName of schemas) {
        let query = (schemaName ? supabase.schema(schemaName) : supabase)
          .from(tableName)
          .select("date_")
          .gte("date_", startOfMonth)
          .lte("date_", endOfMonth);
        if (!hasClinicalAdminPermissions(role)) {
          if (needsSharing) {
            const orParts = [
              `user_id.eq.${userId}`,
              ...sharedUsersOverlapOrClauses(userId),
            ];
            if (ownerIds.length > 0)
              orParts.push(`user_id.in.(${ownerIds.join(",")})`);
            query = query.or(orParts.join(","));
          } else {
            query = query.eq("user_id", userId);
          }
        }
        queries.push(query);
      }
      const results = await Promise.all(queries);
      // With the schemas answering together, an earlier run (the home schema
      // alone, before the selection resolved) can finish after a newer one;
      // it must not overwrite those counts.
      if (cancelled) return;
      const totals = {};
      for (const [i, schemaName] of schemas.entries()) {
        const { data, error } = results[i];
        if (error) {
          console.warn(
            `[CustomCalendar] day counts unavailable for schema ${schemaName}:`,
            error.message,
          );
          continue;
        }
        const label = schemaName || supabaseSchema;
        for (const record of data || []) {
          const day = (totals[record.date_] = totals[record.date_] || {});
          day[label] = (day[label] || 0) + 1;
        }
      }
      setPatientCounts(totals);
    };
    fetchPatientCounts();
    return () => {
      cancelled = true;
    };
  }, [displayedMonth, displayedYear, selectedKey]);

  const goToPrevMonth = () => {
    const newDate = new Date(displayedYear, displayedMonth - 1, 1);
    setDisplayedMonth(newDate.getMonth());
    setDisplayedYear(newDate.getFullYear());
  };

  const goToNextMonth = () => {
    const newDate = new Date(displayedYear, displayedMonth + 1, 1);
    setDisplayedMonth(newDate.getMonth());
    setDisplayedYear(newDate.getFullYear());
  };

  const firstDayOfMonth = new Date(displayedYear, displayedMonth, 1);
  const startingDay = firstDayOfMonth.getDay();
  const daysInMonth = new Date(displayedYear, displayedMonth + 1, 0).getDate();
  let calendarDays = [];

  for (let i = 0; i < startingDay; i++) {
    calendarDays.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(new Date(displayedYear, displayedMonth, i));
  }
  while (calendarDays.length < 42) {
    calendarDays.push(null);
  }

  const schemaColorMap = buildSchemaColorMap(patientCounts);
  const schemaColor = (name) => schemaColorMap[name] || SCHEMA_COLORS[0];

  return (
    <Box p={4} bg="white">
      <Box mb={2}>
        <Flex justifyContent="space-between">
          <Button onClick={goToPrevMonth}>Prev</Button>
          <Button onClick={goToNextMonth}>Next</Button>
        </Flex>
        <Flex justifyContent="space-between" mt={2}>
          <Box fontWeight="bold" color="black">
            {firstDayOfMonth.toLocaleString("en-US", {
              month: "long",
              year: "numeric",
            })}{" "}
            ({timeZoneAbbr} {formattedTime})
          </Box>
          <Flex gap={2}>
            <Button
              onClick={() => onSelectDate(TEST_PATIENT_DATE)}
              bg="#FFF4E5"
              color="#C2410C"
              title={`Jump to test patient fixtures (${TEST_PATIENT_DATE})`}
              _hover={{ bg: "#C2410C", color: "white" }}
            >
              Test
            </Button>
            <Button onClick={() => onSelectDate(today)}>Go to Today</Button>
          </Flex>
        </Flex>
      </Box>
      <Flex justifyContent="space-between" mb={2}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <Box
            key={day}
            w="14.28%"
            textAlign="center"
            fontWeight="bold"
            color="black"
          >
            {day}
          </Box>
        ))}
      </Flex>
      <Grid templateColumns="repeat(7, 1fr)" gap={1}>
        {calendarDays.map((day, index) => {
          if (day === null) {
            return <Box key={index} bg="gray.100" p={2} />;
          }
          const dateString = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(
            day.getDate(),
          ).padStart(2, "0")}`;
          const dayCounts = patientCounts[dateString] || {};
          const dayTotal = totalForDay(dayCounts);
          const hasPatients = dayTotal > 0;
          const isToday = dateString === today;

          const entityLabel = userRole
            ? getLabel(userRole, 'entityPlural')
            : "Patients";

          // With several providers ticked, the total alone doesn't say whose
          // rows they are — break it down by schema, listing only the ones
          // that actually have rows that day.
          const perSchema = contributingSchemas(dayCounts);
          const label =
            perSchema.length >= 1 ? (
              <Box py={1}>
                <Box fontWeight="bold" mb={1}>{`${entityLabel}: ${dayTotal}`}</Box>
                {perSchema.map(([name, count]) => (
                  <Flex key={name} align="center" gap={2}>
                    <Box
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg={schemaColor(name)}
                      flexShrink={0}
                    />
                    <Box flex="1">{`${name}:`}</Box>
                    <Box fontWeight="bold" color={schemaColor(name)}>
                      {count}
                    </Box>
                  </Flex>
                ))}
              </Box>
            ) : (
              `${entityLabel}: ${dayTotal}`
            );

          return (
            <Tooltip
              key={index}
              label={label}
              bg="white"
              color="gray.800"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="md"
            >
              <Box
                bg={isToday ? navy_blue : hasPatients ? turquoise : "white"}
                p={2}
                textAlign="center"
                cursor="pointer"
                borderWidth={isToday ? "2px" : "1px"}
                borderColor={isToday ? royal_blue : "gray.200"}
                color={isToday ? "white" : "black"}
                _hover={{
                  bg: isToday
                    ? royal_blue
                    : hasPatients
                      ? royal_blue
                      : turquoise,
                }}
                onClick={() => onSelectDate(dateString)}
              >
                {day.getDate()}
                {perSchema.length > 1 && (
                  <Flex justify="center" gap="3px" mt="2px">
                    {perSchema.slice(0, 4).map(([name]) => (
                      <Box
                        key={name}
                        w="6px"
                        h="6px"
                        borderRadius="full"
                        bg={schemaColor(name)}
                        borderWidth="1px"
                        borderColor="white"
                      />
                    ))}
                  </Flex>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Grid>
    </Box>
  );
};

export default CustomCalendar;
